import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Country, State, City } from "country-state-city";
import { api, imageUrl, PRIVACY_LEVELS, DOMAINS } from "@/lib/api";
import { resizeImageForUpload } from "@/lib/imageResize";
import LocationPicker from "@/components/LocationPicker";
import RichTextEditor from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UploadSimple, SpinnerGap, Sparkle, X, MapPin, Robot, CheckCircle, ImageSquare, ArrowRight, ShieldCheck,
  NavigationArrow, Tag as TagIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const norm = (s) => (s || "").trim().toLowerCase();

const REUSE_OPTIONS = [
  { v: "private", l: "Private (only me)" },
  { v: "family_only", l: "Family only" },
  { v: "specific_sites", l: "Specific SocialPulse sites" },
  { v: "all_socialpulse", l: "All SocialPulse properties" },
  { v: "public_display", l: "Public display" },
  { v: "public_attribution", l: "Public reuse with attribution" },
  { v: "commercial", l: "Commercial use permitted" },
  { v: "embed_only", l: "Original-link / embed only" },
];
const SITES = [
  { v: "socialpulse", l: "SocialPulse" }, { v: "indianfamilytree", l: "FamilyTree" },
  { v: "alumnipulse", l: "AlumniPulse" }, { v: "travelerpulse", l: "TravelerPulse" },
  { v: "foodpulse", l: "FoodPulse" }, { v: "naturepulse", l: "NaturePulse" },
];
const LOC_CONFIDENCE = {
  L1_exact_gps: { l: "Exact GPS", c: "bg-emerald-100 text-emerald-700" },
  L2_platform: { l: "Platform location", c: "bg-blue-100 text-blue-700" },
  L3_ai_estimated: { l: "AI-estimated", c: "bg-amber-100 text-amber-700" },
  L4_user_confirmed: { l: "User-confirmed", c: "bg-[#F27A54] text-white" },
};

export default function GuidedUpload() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRef = useRef();
  const [step, setStep] = useState("select"); // select | uploading | confirm | done
  const [files, setFiles] = useState([]);      // up to 10 selected files
  const [photo, setPhoto] = useState(null);    // current photo being reviewed
  const activeIdRef = useRef(null);            // guards stale AI polls between batch photos
  const [queue, setQueue] = useState([]);       // remaining photo ids to review after current
  const [reviewedCount, setReviewedCount] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [aiPending, setAiPending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [titleError, setTitleError] = useState(false);

  // form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("");
  const [carry, setCarry] = useState(true);       // "same as previous" for batch review
  const [geoBusy, setGeoBusy] = useState(false);
  const [privacy, setPrivacy] = useState("public");
  const [reuse, setReuse] = useState("private");
  const [sites, setSites] = useState([]);
  const [countryIso, setCountryIso] = useState("");
  const [stateIso, setStateIso] = useState("");
  const [city, setCity] = useState("");
  const [coords, setCoords] = useState({ lat: null, lon: null });

  // United States pinned to the top; the rest alphabetical by name.
  const countries = useMemo(() => {
    const all = Country.getAllCountries();
    const us = all.filter((c) => c.isoCode === "US");
    const rest = all.filter((c) => c.isoCode !== "US").sort((a, b) => a.name.localeCompare(b.name));
    return [...us, ...rest];
  }, []);
  const states = useMemo(() => (countryIso ? State.getStatesOfCountry(countryIso) : []), [countryIso]);
  const cities = useMemo(() => (countryIso && stateIso ? City.getCitiesOfState(countryIso, stateIso) : []), [countryIso, stateIso]);

  const MAX_FILES = 10;
  const addFiles = (list) => {
    const arr = Array.from(list || []).filter(
      (f) => f.type?.startsWith("image/") || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(f.name)
    );
    if (!arr.length) { toast.error("Please choose image files (JPG, PNG, WEBP or iPhone HEIC)."); return; }
    setFiles((prev) => {
      const merged = [...prev, ...arr].slice(0, MAX_FILES);
      if (prev.length + arr.length > MAX_FILES) toast.info(`Up to ${MAX_FILES} photos at a time — extra photos were dropped.`);
      return merged.map((f) => (f.preview ? f : Object.assign(f, { preview: URL.createObjectURL(f) })));
    });
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const nonEmpty = (v) => v && v.replace(/<[^>]*>/g, "").trim() !== "";

  const resetForm = () => {
    setTitle(""); setDescription(""); setDate(""); setTime(""); setTags([]); setTagInput("");
    setNotes(""); setCategory(""); setReuse("private"); setSites([]);
    setCountryIso(""); setStateIso(""); setCity(""); setCoords({ lat: null, lon: null });
    setTitleError(false);
  };

  // Initial prefill from EXIF/date/GPS + any metadata already saved (AI is usually not ready yet).
  // `carried` (optional) applies the previous photo's choices in a batch when "same as previous" is on.
  const prefillFrom = (p, carried = null) => {
    const ai = p.ai || {};
    setNotes(""); setTagInput(""); setTitleError(false);   // per-photo fields never carry
    setTitle(p.title || p.caption || ai.caption || "");
    setDescription(p.description || ai.description || p.original_caption || "");
    setTags(p.manual_tags?.length ? p.manual_tags : (carried?.tags?.length ? carried.tags : (p.keywords || ai.keywords || []).slice(0, 12)));
    // When carrying batch info, the previous photo's chosen category wins over the AI default.
    setCategory(carried?.category || p.primary_domain || "");
    setReuse(carried?.reuse || p.reuse_permission || "private");
    setSites(carried?.sites || p.usable_on_sites || []);
    if (carried?.privacy) setPrivacy(carried.privacy);
    const dt = p.date_taken || (p.original || {}).date_taken;
    if (dt) { const d = new Date(dt); if (!isNaN(d)) { setDate(d.toISOString().slice(0, 10)); setTime(d.toTimeString().slice(0, 5)); } }
    else if (carried?.date) { setDate(carried.date); setTime(carried.time || ""); }
    const loc = p.location || {};
    const gps = (p.original || {}).gps;
    let lat = loc.lat ?? gps?.lat ?? null;
    let lon = loc.lon ?? gps?.lon ?? null;
    // If this photo has no location of its own, carry the previous photo's place.
    if (lat == null && carried) {
      setCoords({ lat: carried.coords?.lat ?? null, lon: carried.coords?.lon ?? null });
      setCountryIso(carried.countryIso || ""); setStateIso(carried.stateIso || ""); setCity(carried.city || "");
    } else {
      setCoords({ lat, lon });
      matchLocation(loc.country, loc.state, loc.city);
    }
  };

  // Merge AI results when they arrive — only into fields the user hasn't filled.
  const mergeAI = (p) => {
    const ai = p.ai || {};
    setTitle((t) => t || p.title || p.caption || ai.caption || "");
    setDescription((d) => (nonEmpty(d) ? d : (p.description || ai.description || "")));
    setTags((prev) => (prev.length ? prev : (p.manual_tags || p.keywords || ai.keywords || []).slice(0, 12)));
    setCoords((c) => {
      if (c.lat != null) return c;
      const loc = p.location || {};
      if (loc.lat != null) { matchLocation(loc.country, loc.state, loc.city); return { lat: loc.lat, lon: loc.lon }; }
      return c;
    });
    setPhoto((cur) => (cur ? { ...cur, ...p } : p));
  };

  // Background AI poll — never blocks the form. Guarded so a poll started for a previous
  // batch photo can't merge its title/description into the photo now on screen.
  const pollAI = async (pid) => {
    setAiPending(true);
    try {
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        if (activeIdRef.current !== pid) return;            // switched photos → abandon
        const p = (await api.get(`/photos/${pid}`)).data;
        if (activeIdRef.current !== pid) return;
        if (p.status !== "analyzing") { mergeAI(p); break; }
      }
    } catch { /* ignore */ }
    finally { if (activeIdRef.current === pid) setAiPending(false); }
  };

  // Show the editable form immediately; kick off AI enrichment in the background.
  const setupPhoto = (p, carried = null) => {
    activeIdRef.current = p.id;
    setPhoto(p);
    prefillFrom(p, carried);
    setStep("confirm");
    setTitleError(false);
    if (p.is_duplicate) toast.warning("Heads up: this looks like a possible duplicate.");
    if (p.status === "analyzing") pollAI(p.id);
  };

  // Import-from-link path: /upload/guided?id=<photoId>. Optional ?queue=<id,id,...>.
  const queueIds = useMemo(() => (searchParams.get("queue") || "").split(",").filter(Boolean), [searchParams]);
  useEffect(() => {
    const pid = searchParams.get("id");
    if (!pid) return;
    setStep("uploading");
    setPhoto(null);
    setQueue(queueIds);
    setBatchTotal(queueIds.length + 1);
    setReviewedCount(0);
    (async () => {
      try {
        const p = (await api.get(`/photos/${pid}`)).data;
        setupPhoto(p);
      } catch { toast.error("Could not load that import"); nav("/upload"); }
    })();
  }, [searchParams]); // eslint-disable-line

  const toggleSite = (v) => setSites((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const matchLocation = (countryName, stateName, cityName) => {
    if (countryName) {
      const c = Country.getAllCountries().find((x) => norm(x.name) === norm(countryName));
      if (c) {
        setCountryIso(c.isoCode);
        if (stateName) {
          const st = State.getStatesOfCountry(c.isoCode).find((x) => norm(x.name) === norm(stateName));
          if (st) setStateIso(st.isoCode);
        }
      }
    }
    if (cityName) setCity(cityName);
  };

  // Upload the selected photos (fast — AI runs after). Review the first, queue the rest.
  const uploadAll = async () => {
    if (!files.length) return;
    setStep("uploading");
    const ids = [];
    let first = null;
    for (let i = 0; i < files.length; i++) {
      let fileToSend = files[i];
      try { fileToSend = await resizeImageForUpload(files[i]); } catch { /* use original */ }
      const fd = new FormData();
      fd.append("file", fileToSend);
      fd.append("privacy", privacy);
      try {
        const { data } = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (i === 0) first = data; else ids.push(data.id);
      } catch (e) {
        toast.error(`${files[i].name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    if (!first) { setStep("select"); return; }
    setQueue(ids);
    setBatchTotal(files.length);
    setReviewedCount(0);
    resetForm();
    setupPhoto(first);
  };

  const advanceQueue = async () => {
    setReviewedCount((c) => c + 1);
    // Snapshot the just-saved photo's choices to carry into the next one (when enabled).
    const carried = carry ? {
      countryIso, stateIso, city, coords, date, time, tags, category, reuse, sites, privacy,
    } : null;
    if (queue.length) {
      const [next, ...rest] = queue;
      setQueue(rest);
      setStep("uploading");
      try { const p = (await api.get(`/photos/${next}`)).data; setupPhoto(p, carried); }
      catch { setStep("done"); }
    } else {
      setStep("done");
    }
  };

  // Use the device's current GPS to fill country/state/city + map pin.
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device."); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lon: longitude });
        try {
          const { data } = await api.get("/geo/reverse", { params: { lat: latitude, lon: longitude } });
          matchLocation(data.country, data.state, data.city);
          toast.success(`Location set${data.city ? ` — ${data.city}` : ""}`);
        } catch { toast.info("Pin placed — add the place names if needed."); }
        setGeoBusy(false);
      },
      () => { toast.error("Couldn't get your location. Please allow location access."); setGeoBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // reverse geocode when the user moves the pin
  const onPinMove = async (lat, lon) => {
    setCoords({ lat, lon });
    try {
      const { data } = await api.get("/geo/reverse", { params: { lat, lon } });
      matchLocation(data.country, data.state, data.city);
      toast.success("Location updated from map");
    } catch (e) { console.debug("reverse geocode skipped", e); }
  };

  const addTags = (raw) => {
    const parts = String(raw || "").split(/[,;\n\r\t]+/).map((t) => t.trim()).filter(Boolean);
    if (!parts.length) return;
    setTags((x) => {
      const set = new Set(x.map((t) => t.toLowerCase()));
      const next = [...x];
      for (const p of parts) if (!set.has(p.toLowerCase())) { next.push(p); set.add(p.toLowerCase()); }
      return next;
    });
  };
  const addTag = () => { addTags(tagInput); setTagInput(""); };
  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
    else if (e.key === "Backspace" && !tagInput && tags.length) setTags((x) => x.slice(0, -1));
  };
  const onTagPaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (/[,;\n\t]/.test(text)) { e.preventDefault(); addTags(text); setTagInput(""); }
  };
  const suggestTags = () => {
    const ai = photo?.ai || {};
    const suggested = [
      ...(photo?.keywords || []), ...(ai.keywords || []),
      ...(photo?.categories || []).map((c) => c.subcategory || c.category).filter(Boolean),
      ...(photo?.objects || []).map((o) => (typeof o === "string" ? o : o.name)).filter(Boolean),
      (photo?.landmark || {}).name,
    ].filter(Boolean);
    if (!suggested.length) { toast.info("No AI tag suggestions available for this photo."); return; }
    const before = tags.length;
    addTags(suggested.join(","));
    setTimeout(() => toast.success("Added AI-suggested tags"), 0);
    if (before === tags.length && suggested.every((s) => tags.map((t) => t.toLowerCase()).includes(s.toLowerCase())))
      toast.info("All AI suggestions are already in your tags.");
  };

  const confirm = async () => {
    if (!nonEmpty(title)) {
      setTitleError(true);
      toast.error("Please add a title before saving.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSaving(true);
    const countryName = countries.find((c) => c.isoCode === countryIso)?.name || null;
    const stateName = states.find((s) => s.isoCode === stateIso)?.name || null;
    let date_taken = null;
    if (date) date_taken = new Date(`${date}T${time || "00:00"}:00`).toISOString();
    try {
      await api.post(`/photos/${photo.id}/confirm`, {
        title, description, notes, tags,
        category: category || null,
        date_taken,
        privacy,
        reuse_permission: reuse,
        usable_on_sites: reuse === "private" || reuse === "family_only" ? [] : sites,
        location: {
          country: countryName, country_code: countryIso || null,
          state: stateName, city: city || null,
          lat: coords.lat, lon: coords.lon,
        },
      });
      toast.success("Photo saved");
      await advanceQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const reset = () => {
    activeIdRef.current = null;
    setStep("select"); setFiles([]); setPhoto(null); setQueue([]); setBatchTotal(0); setReviewedCount(0);
    resetForm();
  };

  const conf = photo?.overall_confidence;

  return (
    <div className="mx-auto max-w-3xl">
      <p className="overline text-[#F27A54]">Import · Guided</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Add a photo</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Your photo uploads instantly and you fill in the details. AI reads GPS &amp; EXIF and suggests a title &amp; description
        <strong> in the background</strong> — you're never blocked waiting for it. Only a title is required; everything else can be edited later.
      </p>

      {/* STEP: SELECT */}
      {step === "select" && (
        <div className="mt-6">
          <div data-testid="guided-dropzone" onClick={() => inputRef.current?.click()}
            className="grid cursor-pointer place-items-center rounded-lg border-2 border-dashed border-border bg-zinc-50 px-6 py-12 text-center hover:border-[#F27A54]">
            <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" data-testid="guided-input"
              onChange={(e) => addFiles(e.target.files)} />
            <UploadSimple size={36} weight="bold" className="text-[#F27A54]" />
            <p className="mt-3 font-display text-lg font-bold">Choose up to {MAX_FILES} photos</p>
            <p className="text-xs text-muted-foreground">JPG · PNG · WEBP · pick several from your library at once</p>
          </div>

          {files.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-sm font-semibold">{files.length} photo{files.length > 1 ? "s" : ""} selected</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {files.map((f, i) => (
                  <div key={i} data-testid={`guided-thumb-${i}`} className="relative overflow-hidden rounded-md border border-border">
                    <img src={f.preview} alt="" className="aspect-square w-full object-cover" />
                    <button type="button" onClick={() => removeFile(i)}
                      className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white hover:bg-red-600">
                      <X size={11} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button data-testid="guided-analyze" onClick={uploadAll} disabled={!files.length}
            className="mt-4 flex items-center gap-2 rounded-md bg-[#F27A54] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9633f] disabled:opacity-50">
            <UploadSimple size={16} weight="fill" /> Upload &amp; add details <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* STEP: UPLOADING */}
      {step === "uploading" && (
        <div className="mt-16 grid place-items-center text-center">
          <SpinnerGap size={44} className="animate-spin text-[#F27A54]" weight="bold" />
          <p className="mt-4 font-display text-lg font-bold">Uploading your photo…</p>
          <p className="text-sm text-muted-foreground">This is quick — AI suggestions arrive afterward while you fill in details.</p>
        </div>
      )}

      {/* STEP: CONFIRM */}
      {step === "confirm" && photo && (
        <div className="mt-6 space-y-6">
          {batchTotal > 1 && (
            <div data-testid="guided-batch-progress" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#F27A54]/30 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-[#F27A54]">
              <span className="flex items-center gap-2"><ImageSquare size={16} weight="fill" /> Photo {reviewedCount + 1} of {batchTotal} · {queue.length} more after this</span>
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-zinc-700" data-testid="guided-carry-toggle">
                <input type="checkbox" checked={carry} onChange={(e) => setCarry(e.target.checked)}
                  className="h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
                Reuse previous photo's place, date, tags &amp; rights
              </label>
            </div>
          )}
          <div className="flex flex-wrap items-start gap-4 rounded-lg border border-border bg-white p-4">
            <img src={imageUrl(photo.id)} alt="" className="h-28 w-28 rounded-md object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {aiPending ? (
                  <span data-testid="guided-ai-pending" className="flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
                    <SpinnerGap size={13} weight="bold" className="animate-spin" /> AI still analyzing…
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 rounded-full bg-[#F27A54]/10 px-2.5 py-1 text-xs font-bold text-[#F27A54]">
                    <Robot size={13} weight="fill" /> AI suggestions ready
                  </span>
                )}
                {conf != null && (
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">{conf}% confidence</span>
                )}
                {photo.location_confidence && LOC_CONFIDENCE[photo.location_confidence] && (
                  <span data-testid="guided-loc-confidence" className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${LOC_CONFIDENCE[photo.location_confidence].c}`}>
                    <MapPin size={12} weight="fill" /> {LOC_CONFIDENCE[photo.location_confidence].l}
                  </span>
                )}
                {photo.primary_domain && <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-600">{photo.primary_domain}</span>}
                {photo.platform && photo.platform !== "upload" && <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-600">via {photo.platform}</span>}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Add a title now (required). AI-drafted title &amp; description will fill in the empty fields when ready — edit anything you like.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="g-title">Title <span className="text-red-600">*</span> <span className="text-muted-foreground">(required)</span></Label>
            <Input id="g-title" data-testid="guided-title" value={title}
              onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(false); }}
              placeholder="A short title for this photo"
              className={`mt-1.5 ${titleError ? "border-red-500 focus-visible:ring-red-500" : ""}`} />
            {titleError && <p data-testid="guided-title-error" className="mt-1 text-xs text-red-600">A title is required to save this photo.</p>}
          </div>

          <div>
            <Label htmlFor="g-desc">Description <span className="text-muted-foreground">(optional · AI drafted · formatting supported)</span></Label>
            <div className="mt-1.5">
              <RichTextEditor testid="guided-description" value={description} onChange={setDescription} placeholder="Describe what's in this photo…" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="g-date">Date</Label>
              <Input id="g-date" data-testid="guided-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="g-time">Time <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="g-time" data-testid="guided-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="g-tags">Tags <span className="text-muted-foreground">(type, or paste a comma-separated list)</span></Label>
              <button type="button" data-testid="guided-suggest-tags" onClick={suggestTags}
                className="flex items-center gap-1 rounded-md border border-[#F27A54]/30 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-[#F27A54] hover:bg-blue-100">
                <Sparkle size={13} weight="fill" /> Suggest tags
              </button>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5 rounded-md border border-border bg-white p-2 focus-within:ring-1 focus-within:ring-[#F27A54]">
              {tags.map((t) => (
                <span key={t} data-testid={`guided-tag-${t}`} className="flex items-center gap-1 rounded-sm bg-blue-50 px-2 py-0.5 text-xs font-medium text-[#F27A54]">
                  {t}<button type="button" onClick={() => setTags((x) => x.filter((v) => v !== t))} className="hover:text-red-600"><X size={11} weight="bold" /></button>
                </span>
              ))}
              <input id="g-tags" data-testid="guided-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={onTagKey} onPaste={onTagPaste} onBlur={addTag} placeholder={tags.length ? "" : "Type a tag, press Enter"}
                className="min-w-[120px] flex-1 border-none bg-transparent text-sm outline-none" />
            </div>
          </div>

          <div>
            <Label htmlFor="g-category" className="flex items-center gap-1.5"><TagIcon size={14} weight="fill" className="text-[#F27A54]" /> Category <span className="text-muted-foreground">(pick one — no typing)</span></Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="g-category" data-testid="guided-category-select" className="mt-1.5 h-10 w-full rounded-md sm:w-72"><SelectValue placeholder="Choose a category…" /></SelectTrigger>
              <SelectContent>
                {DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="g-notes">Notes / comments <span className="text-muted-foreground">(optional · formatting supported)</span></Label>
            <div className="mt-1.5">
              <RichTextEditor testid="guided-notes" value={notes} onChange={setNotes} placeholder="Anything else worth recording…" />
            </div>
          </div>

          {/* Location */}
          <div className="rounded-lg border border-border bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <MapPin size={18} weight="fill" className="text-[#F27A54]" />
              <h3 className="font-display text-sm font-bold">Location</h3>
              {photo.location_source && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-zinc-500">from {photo.location_source}</span>}
              <button type="button" data-testid="guided-use-location" onClick={useMyLocation} disabled={geoBusy}
                className="ml-auto flex items-center gap-1.5 rounded-md border border-[#F27A54]/40 bg-blue-50 px-2.5 py-1 text-xs font-bold text-[#F27A54] hover:bg-blue-100 disabled:opacity-60">
                {geoBusy ? <SpinnerGap size={13} weight="bold" className="animate-spin" /> : <NavigationArrow size={13} weight="fill" />}
                Use my location
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Country</Label>
                <select data-testid="guided-country" value={countryIso}
                  onChange={(e) => { setCountryIso(e.target.value); setStateIso(""); setCity(""); }}
                  className="mt-1.5 h-10 w-full rounded-md border border-border bg-white px-2 text-sm outline-none focus:ring-1 focus:ring-[#F27A54]">
                  <option value="">Select country…</option>
                  {countries.map((c) => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">State / Province</Label>
                <select data-testid="guided-state" value={stateIso} disabled={!countryIso}
                  onChange={(e) => { setStateIso(e.target.value); setCity(""); }}
                  className="mt-1.5 h-10 w-full rounded-md border border-border bg-white px-2 text-sm outline-none focus:ring-1 focus:ring-[#F27A54] disabled:bg-zinc-50">
                  <option value="">{countryIso ? "Select state…" : "Pick country first"}</option>
                  {states.map((s) => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">City / Village</Label>
                <input data-testid="guided-city" list="guided-cities" value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="Type or pick" className="mt-1.5 h-10 w-full rounded-md border border-border bg-white px-3 text-sm outline-none focus:ring-1 focus:ring-[#F27A54]" />
                <datalist id="guided-cities">
                  {cities.slice(0, 300).map((c) => <option key={`${c.name}-${c.latitude}`} value={c.name} />)}
                </datalist>
              </div>
            </div>

            <div className="mt-4">
              <Label className="text-xs">Position on map <span className="text-muted-foreground">(drag the pin or click to correct)</span></Label>
              <div className="mt-1.5">
                <LocationPicker lat={coords.lat} lon={coords.lon} onMove={onPinMove} />
              </div>
              {coords.lat != null
                ? <p className="mt-1 text-xs text-muted-foreground">{coords.lat.toFixed(5)}, {coords.lon.toFixed(5)}</p>
                : <p className="mt-1 text-xs text-amber-600">No GPS found — click the map to place this photo.</p>}
            </div>
          </div>

          {/* Rights & reuse */}
          <div className="rounded-lg border border-border bg-white p-4" data-testid="guided-rights-card">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck size={18} weight="fill" className="text-[#F27A54]" />
              <h3 className="font-display text-sm font-bold">Rights &amp; reuse</h3>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              Who is allowed to reuse this photo across the SocialPulse network? This controls whether other sites can pull it via the media API.
            </p>
            <label className="mb-3 flex cursor-pointer items-start gap-2.5 rounded-md border border-[#F27A54]/30 bg-blue-50/60 p-3" data-testid="guided-make-public">
              <input type="checkbox"
                checked={reuse === "public_attribution" && privacy === "public"}
                onChange={(e) => {
                  if (e.target.checked) { setReuse("public_attribution"); setPrivacy("public"); }
                  else { setReuse("private"); setPrivacy("public"); }
                }}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
              <span>
                <span className="block text-sm font-bold text-[#F27A54]">Make this photo public</span>
                <span className="mt-0.5 block text-xs text-zinc-600">Publishes it to the public gallery on save (with attribution). A Title is required.</span>
              </span>
            </label>
            <div>
              <Label className="text-xs">Reuse permission</Label>
              <Select value={reuse} onValueChange={setReuse}>
                <SelectTrigger data-testid="guided-reuse" className="mt-1.5 h-10 w-full rounded-md sm:w-80"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REUSE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {reuse !== "private" && reuse !== "family_only" && (
              <div className="mt-4" data-testid="guided-sites-group">
                <Label className="text-xs">
                  {reuse === "specific_sites" ? "Which sites can use this photo?" : "Prefer specific sites (optional)"}
                </Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {SITES.map((s) => {
                    const on = sites.includes(s.v);
                    return (
                      <button
                        type="button"
                        key={s.v}
                        data-testid={`guided-site-${s.v}`}
                        onClick={() => toggleSite(s.v)}
                        className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-xs font-semibold transition-colors ${
                          on ? "border-[#F27A54] bg-blue-50 text-[#F27A54]" : "border-border bg-white text-zinc-600 hover:border-[#F27A54]/50"
                        }`}
                      >
                        <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-sm border ${on ? "border-[#F27A54] bg-[#F27A54] text-white" : "border-zinc-300"}`}>
                          {on && <CheckCircle size={12} weight="fill" />}
                        </span>
                        {s.l}
                      </button>
                    );
                  })}
                </div>
                {reuse === "specific_sites" && !sites.length && (
                  <p className="mt-2 text-xs text-amber-600">Pick at least one site, or this photo won't be reusable anywhere.</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Label className="text-xs">Visibility</Label>
            <Select value={privacy} onValueChange={setPrivacy}>
              <SelectTrigger data-testid="guided-privacy" className="h-9 w-44 rounded-md"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIVACY_LEVELS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <button onClick={reset} className="rounded-md border border-border px-4 py-2.5 text-sm font-semibold text-zinc-600 hover:bg-zinc-100">Discard</button>
            <button data-testid="guided-confirm" onClick={confirm} disabled={saving}
              className="flex items-center gap-2 rounded-md bg-[#F27A54] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9633f] disabled:opacity-60">
              <CheckCircle size={16} weight="fill" /> {saving ? "Saving…" : (queue.length ? "Save & next photo" : "Save photo")}
            </button>
          </div>
        </div>
      )}

      {/* STEP: DONE */}
      {step === "done" && photo && (
        <div className="mt-16 grid place-items-center text-center">
          <CheckCircle size={52} className="text-emerald-500" weight="fill" />
          <p className="mt-4 font-display text-xl font-bold">{batchTotal > 1 ? `${batchTotal} photos saved` : "Photo saved"}</p>
          <p className="text-sm text-muted-foreground">AI is finishing up any remaining details in the background.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <button onClick={() => nav(`/photos/${photo.id}`)} className="rounded-md bg-[#F27A54] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d9633f]">View photo</button>
            <button onClick={() => nav("/my-uploads")} className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100">My uploads</button>
            <button data-testid="guided-another" onClick={reset} className="flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-sm font-semibold text-[#F27A54] hover:bg-blue-50"><ImageSquare size={15} weight="bold" /> Add another</button>
          </div>
        </div>
      )}
    </div>
  );
}
